// Executive Dashboard page - main overview for executives
// Shows KPIs, AI insights, and forecast


/**
 * NOTES SUMMARY - EXECUTIVE DASHBOARD
 * 
 * What this page does:
 *  - Shows KPI cards (Total Leads, Cold/Engaged/Qualified, Average Score)
 *  - Shows AI insights from Claude
 *  - Shows pipeline forecast (30/60/90 day predictions)
 *  - Refresh button reloads all data
 * 
 */




import React, { useState, useEffect } from 'react';
import Header from '../components/layout/Header';
import Sidebar from '../components/layout/Sidebar';
import KPICard from '../components/dashboard/KPICard';
import AIInsights from '../components/dashboard/AIInsights';
import PipelineForecast from '../components/PipelineForecast';
import CampaignMetrics from '../components/dashboard/CampaignMetrics';
import GA4Analytics from '../components/dashboard/GA4Analytics';
import Modal from '../components/common/Modal';
import { getLeads, getInsights, getCampaignStats, getForecast, getNewsletterSubscribers } from '../utils/api';
import { COLORS } from '../utils/constants';
import '../styles/App.css';

function ExecutiveDashboard() {
  // State for data
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [newsletterData, setNewsletterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [campaignLoading, setCampaignLoading] = useState(true);
  const [campaignError, setCampaignError] = useState(null);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info' });
  
  // Mobile menu state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  const closeSidebar = () => {
    setSidebarOpen(false);
  };
  
  // Load data when component mounts
  useEffect(() => {
    loadDashboardData();
  }, []);
  
  // Function to load all dashboard data
  const loadDashboardData = async () => {
    setLoading(true);
    setCampaignLoading(true);
    setCampaignError(null);

    try {
      // Fetch leads, insights, forecast, and newsletter in parallel
      // Use Promise.allSettled to load data even if some endpoints fail
      const results = await Promise.allSettled([
        getLeads(),
        getInsights(),
        getForecast(),
        getNewsletterSubscribers()
      ]);

      // Set state with data from successful requests
      if (results[0].status === 'fulfilled') {
        setStats(results[0].value.stats);
      } else {
        console.error('Failed to load leads:', results[0].reason);
      }

      if (results[1].status === 'fulfilled') {
        setInsights(results[1].value.dailyInsights);
      } else {
        console.error('Failed to load insights:', results[1].reason);
      }

      if (results[2].status === 'fulfilled') {
        setForecastData(results[2].value.forecast);
      } else {
        console.error('Failed to load forecast:', results[2].reason);
      }

      if (results[3].status === 'fulfilled') {
        setNewsletterData(results[3].value);
      } else {
        console.error('Failed to load newsletter subscribers:', results[3].reason);
        // Set default newsletter data so the UI doesn't break
        setNewsletterData({ subscriberCount: 0 });
      }

    } catch (error) {
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to load dashboard: ' + error.message,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
    
    // Load campaign data separately to avoid blocking main dashboard
    try {
      const campaignStats = await getCampaignStats();
      setCampaignData(campaignStats);
    } catch (error) {
      console.error('Failed to load campaign data:', error);
      setCampaignError(error.message);
    } finally {
      setCampaignLoading(false);
    }
    
  };
  
  // Show loading state
  if (loading) {
    return (
      <div className="app-container">
        <Header onMenuClick={toggleSidebar} />
        <div className="app-body">
          <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
          <main className="app-main">
            <div className="loading">Loading dashboard...</div>
          </main>
        </div>
      </div>
    );
  }
  
  return (
    <div className="app-container">
      <Header onMenuClick={toggleSidebar} />
      <div className="app-body">
        <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        <main className="app-main">
          <div className="page-header">
            <h1>Executive Dashboard</h1>
            <button onClick={loadDashboardData} className="btn btn-secondary">
              Refresh Data
            </button>
          </div>
          
          {/* KPI Cards Row */}
          <div className="kpi-grid">
            <KPICard
              title="Total Leads"
              value={stats?.totalLeads || 0}
              color={COLORS.primary}
              tooltip="Total number of leads in your database from all sources."
            />
            <KPICard
              title="Cold Leads"
              value={stats?.coldLeads || 0}
              color={COLORS.gray[500]}
              tooltip="Leads who have not yet shown significant engagement with your emails."
            />
            <KPICard
              title="Engaged Leads"
              value={stats?.engagedLeads || 0}
              color={COLORS.warning}
              tooltip="Leads who have opened emails, clicked links, or shown interest."
            />
            <KPICard
              title="Qualified Leads"
              value={stats?.qualifiedLeads || 0}
              color={COLORS.success}
              tooltip="Sales-ready leads that meet your qualification criteria."
            />
            <KPICard
              title="Average Score"
              value={stats?.averageScore?.toFixed(1) || 'N/A'}
              color={COLORS.secondary}
              tooltip="The average AI-generated score across all leads. Scores range from 0-100."
            />
            <KPICard
              title="Newsletter Subscribers"
              value={newsletterData?.subscriberCount || 0}
              color={COLORS.warning}
              tooltip="Contacts who submitted the website contact form."
            />
          </div>
          
          {/* AI Insights Section */}
          <div className="dashboard-section">
            <AIInsights 
              insights={insights} 
              date={new Date()}
            />
          </div>
          
          {/* Pipeline Forecast Section */}
          <div className="dashboard-section">
            <PipelineForecast forecast={forecastData} />
          </div>
          
          {/* Campaign Metrics Section */}
          <div className="dashboard-section">
            <CampaignMetrics 
              campaignData={campaignData}
              loading={campaignLoading}
              error={campaignError}
            />
          </div>
          
          {/* GA4 Analytics Section */}
          <div className="dashboard-section">
            <GA4Analytics />
          </div>
          
          {/* Error Modal */}
          <Modal
            isOpen={modal.isOpen}
            onClose={() => setModal({ ...modal, isOpen: false })}
            title={modal.title}
            message={modal.message}
            type={modal.type}
          />
        </main>
      </div>
    </div>
  );
}

export default ExecutiveDashboard;